import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, Trophy } from 'lucide-react';
import { api } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScorePill } from '../components/coach/CoachScore';
import { useCoachT } from '../lib/coachI18n';

const TXT = {
  it: { title: 'Coach AI — valutazioni call', sub: 'Classifica del team e ultime call analizzate', board: 'Classifica', recent: 'Ultime call', agent: 'Agente', calls: 'Call', avg: 'Media', best: 'Migliore', client: 'Cliente', none: 'Nessuna call analizzata nel periodo.', days: 'giorni', mine: 'Le tue call analizzate' },
  es: { title: 'Coach IA — evaluaciones', sub: 'Ranking del equipo y últimas llamadas analizadas', board: 'Ranking', recent: 'Últimas llamadas', agent: 'Agente', calls: 'Llamadas', avg: 'Media', best: 'Mejor', client: 'Cliente', none: 'Ninguna llamada analizada en el periodo.', days: 'días', mine: 'Tus llamadas analizadas' },
};

const Board = ({ rows, x }) => (
  <div className="rounded-xl border border-border bg-[var(--surface-1)] overflow-hidden" data-testid="coach-leaderboard">
    <div className="px-4 py-2.5 border-b border-border text-sm font-medium flex items-center gap-2"><Trophy className="w-4 h-4 text-[#C9A227]" />{x.board}</div>
    <table className="w-full text-sm">
      <thead className="text-[11px] uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-4 py-2">#</th><th className="text-left">{x.agent}</th><th>{x.calls}</th><th>{x.avg}</th><th className="pr-4">{x.best}</th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.user_id} className="border-t border-border" data-testid={`coach-board-row-${i}`}>
            <td className="px-4 py-2 font-serif text-lg text-[#C9A227]">{i + 1}</td>
            <td>{r.name}</td><td className="text-center">{r.calls}</td>
            <td className="text-center"><ScorePill score={r.avg} /></td><td className="text-center pr-4">{r.best}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function CoachPage() {
  const { lang } = useCoachT();
  const x = TXT[lang] || TXT.it;
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);

  useEffect(() => { api.get('/coach/overview', { params: { days } }).then((r) => setData(r.data)); }, [days]);

  return (
    <div className="p-4 md:p-8 max-w-6xl space-y-6" data-testid="coach-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#0F2A22] text-[#C9A227] flex items-center justify-center"><Award className="w-6 h-6" /></div>
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[hsl(var(--primary))]">{x.title}</h1>
            <p className="text-sm text-muted-foreground">{data?.is_manager ? x.sub : x.mine}</p>
          </div>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-36" data-testid="coach-days-select"><SelectValue /></SelectTrigger>
          <SelectContent>{['7', '30', '90', '365'].map((d) => <SelectItem key={d} value={d}>{d} {x.days}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {data && !data.recent.length && <div className="text-sm text-muted-foreground" data-testid="coach-page-empty">{x.none}</div>}
      {data && data.leaderboard.length > 0 && <Board rows={data.leaderboard} x={x} />}
      {data && data.recent.length > 0 && (
        <div className="rounded-xl border border-border bg-[var(--surface-1)]" data-testid="coach-recent">
          <div className="px-4 py-2.5 border-b border-border text-sm font-medium">{x.recent}</div>
          {data.recent.map((r) => (
            <Link key={r.id} to={`/app/leads/${r.lead_id}`} className="flex items-center gap-3 px-4 py-2.5 border-t border-border first:border-t-0 hover:bg-[var(--surface-2)] transition-colors" data-testid={`coach-recent-${r.id}`}>
              <ScorePill score={r.score} />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate"><b>{r.lead_name || x.client}</b> · {r.verdict}</div>
                <div className="text-[11px] text-muted-foreground">{r.user_name} · {(r.created_at || '').slice(0, 16).replace('T', ' ')}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
